import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import MagicBentoCard from '../components/MagicBentoCard';
import useHeroEntrance from '../hooks/useHeroEntrance';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  FaCheckCircle, FaUsers, FaLightbulb, FaAward,
  FaRocket, FaCode, FaHeadset,
} from 'react-icons/fa';
import { HiArrowRight, HiSparkles } from 'react-icons/hi';
import { prefersReducedMotion } from '../hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const navigate = useNavigate();
  const heroBadgeRef = useRef(null);
  const heroH1Ref = useRef(null);
  const heroParaRef = useRef(null);
  useHeroEntrance([
    heroBadgeRef,
    { ref: heroH1Ref, heading: true },
    heroParaRef,
  ]);

  const values = [
    { icon: <FaUsers />, title: 'Client-Focused', description: "We prioritize our clients' needs and goals, delivering solutions that drive real business value.", color: 'from-nexcess-blue to-nexcess-cyan' },
    { icon: <FaLightbulb />, title: 'Innovation', description: 'We embrace emerging technologies and creative approaches to solve complex challenges.', color: 'from-nexcess-cyan to-nexcess-purple' },
    { icon: <FaAward />, title: 'Excellence', description: 'We strive for excellence in every project, maintaining the highest standards of quality.', color: 'from-nexcess-purple to-nexcess-blue' },
    { icon: <FaRocket />, title: 'Agility', description: 'We adapt quickly to changes, ensuring timely delivery without compromising on quality.', color: 'from-nexcess-blue to-nexcess-purple' },
    { icon: <FaCode />, title: 'Technical Expertise', description: 'Our team consists of skilled professionals with deep technical knowledge and experience.', color: 'from-nexcess-cyan to-nexcess-blue' },
    { icon: <FaHeadset />, title: 'Reliable Support', description: 'We provide ongoing support and maintenance to ensure the continued success of our solutions.', color: 'from-nexcess-purple to-nexcess-cyan' },
  ];

  const stats = [
    { value: '5+', label: 'Years of Experience', sub: 'Delivering cutting-edge solutions' },
    { value: '204+', label: 'Projects Completed', sub: 'Across various industries' },
    { value: '10+', label: 'Team Members', sub: 'Skilled professionals' },
  ];

  /* ── Stats GSAP refs ── */
  const statsWrapRef  = useRef(null);
  const statsCardRefs = useRef([]);
  const statsValueRefs = useRef([]);

  /* ── Story + Values refs ── */
  const storyImagesRef = useRef(null);
  const valuesWrapRef  = useRef(null);

  /* Story images: scroll-driven parallax (col1 drifts up, col2 drifts down) */
  useEffect(() => {
    if (prefersReducedMotion() || !storyImagesRef.current) return;
    const root = storyImagesRef.current;
    const col1 = root.querySelector('.story-col-1');
    const col2 = root.querySelector('.story-col-2');
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: root,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1,
        onUpdate: ({ progress }) => {
          const p = progress - 0.5;
          if (col1) gsap.to(col1, { y: p * -60, duration: 0.4, overwrite: 'auto', ease: 'power1.out' });
          if (col2) gsap.to(col2, { y: p *  60, duration: 0.4, overwrite: 'auto', ease: 'power1.out' });
        },
      });
    }, storyImagesRef);
    return () => ctx.revert();
  }, []);

  /* Values: GSAP staggered entrance before MagicBento hover takes over */
  useEffect(() => {
    if (!valuesWrapRef.current) return;
    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray('.value-card');
      if (!cards.length) return;
      if (prefersReducedMotion()) {
        gsap.set(cards, { opacity: 1, y: 0, scale: 1 });
        return;
      }
      gsap.set(cards, { opacity: 0, y: 40, scale: 0.95, willChange: 'transform' });
      ScrollTrigger.create({
        trigger: valuesWrapRef.current,
        start: 'top 80%',
        once: true,
        onEnter: () => {
          gsap.to(cards, {
            opacity: 1, y: 0, scale: 1,
            duration: 0.95, stagger: 0.1, ease: 'power3.out',
          });
        },
      });
    }, valuesWrapRef);
    return () => ctx.revert();
  }, []);

  /* ── Stats: iris clip-path reveal + slot-reel digit roll + rotating conic-gradient border on hover ── */
  useEffect(() => {
    const wrap  = statsWrapRef.current;
    const cards = statsCardRefs.current.filter(Boolean);
    if (!wrap || !cards.length) return;

    // Inject keyframes for the rotating conic border (only once)
    if (!document.getElementById('about-stats-styles')) {
      const style = document.createElement('style');
      style.id = 'about-stats-styles';
      style.textContent = `
        @property --stat-angle { syntax: '<angle>'; inherits: false; initial-value: 0deg; }
        .stat-card { position: relative; isolation: isolate; }
        .stat-card::before {
          content: '';
          position: absolute; inset: -2px;
          padding: 2px;
          border-radius: inherit;
          background: conic-gradient(from var(--stat-angle, 0deg),
            rgba(9,132,227,0) 0deg,
            rgba(9,132,227,0.7) 60deg,
            rgba(0,176,255,0.85) 120deg,
            rgba(168,0,255,0.7) 200deg,
            rgba(9,132,227,0) 320deg);
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor; mask-composite: exclude;
          opacity: 0; transition: opacity 0.45s ease;
          pointer-events: none; z-index: 0;
        }
        .stat-card:hover::before,
        .stat-card.stat-spin-once::before { opacity: 1; animation: stat-spin 4s linear infinite; }
        @keyframes stat-spin { to { --stat-angle: 360deg; } }
        .stat-card > * { position: relative; z-index: 1; }
        .stat-reel { display: inline-flex; overflow: hidden; line-height: 0.95; }
        .stat-digit { display: inline-block; will-change: transform; }
      `;
      document.head.appendChild(style);
    }

    // Initial state: cards collapsed via clip-path iris (closed)
    gsap.set(cards, {
      clipPath: 'circle(0% at 50% 50%)',
      opacity: 0,
      willChange: 'clip-path, transform, opacity',
    });

    // Mask the value text initially — slides down into view from above
    statsValueRefs.current.forEach((el) => {
      if (!el) return;
      gsap.set(el, { clipPath: 'inset(0 0 100% 0)', y: -10 });
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: wrap, start: 'top 85%', once: true,
        onEnter: () => {
          cards.forEach((c) => c.classList.add('stat-spin-once'));
          // Remove after one full rotation so border returns to hover-only mode
          setTimeout(() => cards.forEach((c) => c.classList.remove('stat-spin-once')), 4000);
        },
      },
    });

    // Iris-open the cards in stagger
    tl.to(cards, {
      clipPath: 'circle(120% at 50% 50%)',
      opacity: 1,
      duration: 1.2,
      stagger: 0.18,
      ease: 'power3.out',
    });

    // Value text: clip-path mask slides down to reveal, then a quick scale punch
    statsValueRefs.current.forEach((el, i) => {
      if (!el) return;
      tl.to(el, {
        clipPath: 'inset(0 0 0% 0)',
        y: 0,
        duration: 0.9,
        ease: 'expo.out',
      }, 0.3 + i * 0.18);
      tl.fromTo(el,
        { scale: 1 },
        { scale: 1.08, duration: 0.25, ease: 'power2.out', yoyo: true, repeat: 1 },
        `>-0.1`
      );
    });

    // Smooth hover lift
    const handlers = cards.map((card) => {
      const onEnter = () => gsap.to(card, { y: -10, scale: 1.025, duration: 0.55, ease: 'power3.out', overwrite: 'auto' });
      const onLeave = () => gsap.to(card, { y: 0,  scale: 1,     duration: 0.85, ease: 'power3.out', overwrite: 'auto' });
      card.addEventListener('mouseenter', onEnter);
      card.addEventListener('mouseleave', onLeave);
      return { card, onEnter, onLeave };
    });

    return () => {
      tl.kill();
      handlers.forEach(({ card, onEnter, onLeave }) => {
        card.removeEventListener('mouseenter', onEnter);
        card.removeEventListener('mouseleave', onLeave);
      });
    };
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative pt-36 pb-20 overflow-hidden bg-paper">
        <div className="absolute inset-0 bg-mesh-soft pointer-events-none" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-nexcess-blue/8 blur-3xl animate-blob pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-nexcess-purple/8 blur-3xl animate-blob pointer-events-none" style={{ animationDelay: '4s' }} />

        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div ref={heroBadgeRef} className="badge mb-5 mx-auto">
              <HiSparkles /> About Nexcess Infotech
            </div>
            <h1 ref={heroH1Ref} className="display-hero mb-6">
              Crafting <span className="gradient-text">Digital Experiences</span> That Matter
            </h1>
            <p ref={heroParaRef} className="narrative-lg">
              We're a team of passionate tech enthusiasts dedicated to transforming
              businesses through innovative digital solutions.
            </p>
          </div>

          {/* Stats cards */}
          <div ref={statsWrapRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((s, i) => (
              <div
                key={i}
                ref={(el) => { statsCardRefs.current[i] = el; }}
                className="stat-card bg-white rounded-2xl p-7 border border-hairline shadow-soft cursor-pointer"
                style={{ willChange: 'transform' }}
              >
                <div
                  ref={(el) => { statsValueRefs.current[i] = el; }}
                  data-value={s.value}
                  className="text-5xl md:text-6xl font-extrabold gradient-text mb-3 leading-none tracking-tight"
                >
                  {s.value}
                </div>
                <div className="font-semibold text-ink mb-1">{s.label}</div>
                <div className="text-sm text-ink-soft">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="section-padding bg-paper-2">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div ref={storyImagesRef} className="grid grid-cols-2 gap-4">
                <div className="story-col-1 space-y-4" style={{ willChange: 'transform' }}>
                  <div className="rounded-2xl overflow-hidden shadow-soft-lg" style={{ aspectRatio: '4/5' }}>
                    <img
                      src="https://humanyze.com/wp-content/uploads/2021/06/cross-team-collaboration-min.jpg"
                      alt="Team collaboration"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>
                <div className="story-col-2 space-y-4 pt-10" style={{ willChange: 'transform' }}>
                  <div className="rounded-2xl overflow-hidden shadow-soft-lg" style={{ aspectRatio: '4/3' }}>
                    <img
                      src="https://images.unsplash.com/photo-1531482615713-2afd69097998?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                      alt="Office environment"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-soft-lg" style={{ aspectRatio: '4/3' }}>
                    <img
                      src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                      alt="Team meeting"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>
              </div>
              {/* Floating glass card */}
              <motion.div
                className="absolute -bottom-6 -right-4 glass rounded-2xl p-5 shadow-soft-lg min-w-[180px]"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-nexcess-blue animate-pulse" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-nexcess-blue">Est. 2020</span>
                </div>
                <div className="text-3xl font-extrabold gradient-text leading-none">5+</div>
                <div className="text-xs font-semibold text-ink mt-1">Years of Combined</div>
                <div className="text-xs text-ink-soft">Experience</div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div data-reveal="up" data-reveal-delay="0.05" className="badge mb-5">Our Story</div>
              <h2 data-reveal="wipe" className="heading-xl mb-6">
                The Journey of <span className="gradient-text">Excellence</span> and Innovation
              </h2>
              <p className="narrative-body mb-5">
                Founded in 2020, Nexcess Infotech began as a small team of developers with a big
                vision: to help businesses leverage technology for growth and innovation. What
                started as a web development agency has evolved into a comprehensive digital
                solutions provider, offering services across various domains including web and
                mobile app development, cloud solutions, AI integration, and more.
              </p>
              <p className="narrative-body mb-8">
                Over the years, we've partnered with businesses of all sizes, from startups to
                enterprises, helping them transform their digital presence and achieve their
                business goals. Our commitment to quality, innovation, and client satisfaction
                has been the cornerstone of our success and growth.
              </p>

              <ul className="space-y-4">
                {[
                  ['Global Presence', 'Serving clients across North America, Europe, and Asia'],
                  ['Industry Expertise', 'Deep experience in finance, healthcare, e-commerce, and more'],
                  ['Continuous Learning', 'Staying at the forefront of technological advancements'],
                ].map(([t, s]) => (
                  <li key={t} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-hairline shadow-soft">
                    <FaCheckCircle className="text-nexcess-blue mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <div className="font-semibold text-ink mb-0.5">{t}</div>
                      <div className="text-sm text-ink-soft">{s}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section-padding bg-paper">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <div data-reveal="up" data-reveal-delay="0.05" className="badge mb-4">Our Values</div>
            <h2 data-reveal="wipe" className="heading-xl mb-5">
              Principles That <span className="gradient-text">Guide</span> Our Work
            </h2>
            <p data-reveal="up" data-reveal-delay="0.2" className="narrative-lg">
              At Nexcess Infotech, our core values shape how we approach every project and client relationship.
            </p>
          </div>

          <div ref={valuesWrapRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value) => (
              <div key={value.title} className="value-card" style={{ willChange: 'transform' }}>
                <MagicBentoCard
                  className="group bg-white rounded-2xl p-7 border border-hairline shadow-soft h-full"
                  glowColor="9, 132, 227"
                  particleCount={10}
                  enableTilt={true}
                  enableMagnetism={true}
                  enableSpotlight={true}
                  enableBorderGlow={true}
                  enableStars={true}
                  clickEffect={true}
                >
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center text-white text-2xl mb-5 shadow-soft group-hover:scale-110 transition-transform duration-300 relative z-10`}>
                    {value.icon}
                  </div>
                  <h3 className="heading-sm mb-3 relative z-10">{value.title}</h3>
                  <p className="narrative-body relative z-10">{value.description}</p>
                </MagicBentoCard>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-paper">
        <div className="container-custom">
          <div className="relative overflow-hidden rounded-3xl p-10 md:p-16 text-center text-white shadow-soft-lg" style={{ background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 50%, #d500f9 100%)' }}>
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-5 text-white">
                Let's Build Something Great Together
              </h2>
              <p className="text-white/90 text-lg mb-8">
                Partner with us and turn your vision into a polished digital reality.
              </p>
              <button
                onClick={() => navigate('/contact')}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white text-nexcess-blue font-semibold shadow-soft hover:-translate-y-0.5 hover:shadow-2xl transition-all duration-300"
              >
                Get in Touch <HiArrowRight />
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
